import { Head, Link, router } from '@inertiajs/react'
import { Plus } from 'lucide-react'
import type { ReactNode } from 'react'

import { PageHeader } from '@/components/PageHeader'
import { Pagination } from '@/components/Pagination'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { DashboardLayout } from '@/layouts/DashboardLayout'
import { fundPath, fundsPath, newFundPath } from '@/lib/routes'
import type { PaginationMeta } from '@/types/inertia'

type FundRow = {
  id: number
  name: string
  slug: string
  vintage_year: number
  investment_count: number
  commitment_total: string
}

type FundsIndexProps = {
  funds: FundRow[]
  pagination: PaginationMeta
}

function formatMoney(value: string): string {
  const num = Number(value)
  if (Number.isNaN(num)) return value
  return num.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
}

function FundsIndex({ funds, pagination }: FundsIndexProps) {
  return (
    <>
      <Head title="Funds" />

      <div className="mx-auto max-w-6xl px-8 py-10">
        <PageHeader
          title="Funds"
          description="Each fund — its vintage, commitments, invested capital and portfolio."
          actions={
            <Link href={newFundPath()}>
              <Button>
                <Plus />
                New fund
              </Button>
            </Link>
          }
        />

        <div className="mb-3 flex items-center justify-end text-xs text-muted-foreground">
          <span className="tabular-nums">{pagination.total_count} total</span>
        </div>

        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="border-b border-border text-left text-xs uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="px-6 py-3 font-medium">Name</th>
                  <th className="px-6 py-3 font-medium">Slug</th>
                  <th className="px-6 py-3 font-medium text-right">Vintage</th>
                  <th className="px-6 py-3 font-medium text-right">Investments</th>
                  <th className="px-6 py-3 font-medium text-right">Committed</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {funds.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-6 py-10 text-center text-muted-foreground">
                      No funds yet.
                    </td>
                  </tr>
                )}
                {funds.map((f) => (
                  <tr
                    key={f.id}
                    className="cursor-pointer transition-colors hover:bg-muted/50"
                    onClick={() => router.visit(fundPath(f.slug))}
                  >
                    <td className="px-6 py-4 font-medium">{f.name}</td>
                    <td className="px-6 py-4 text-muted-foreground">{f.slug}</td>
                    <td className="px-6 py-4 text-right tabular-nums">{f.vintage_year}</td>
                    <td className="px-6 py-4 text-right tabular-nums text-muted-foreground">
                      {f.investment_count}
                    </td>
                    <td className="px-6 py-4 text-right tabular-nums text-muted-foreground">
                      {formatMoney(f.commitment_total)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        <div className="mt-4">
          <Pagination
            basePath={fundsPath()}
            currentPage={pagination.current_page}
            nextPage={pagination.next_page}
            prevPage={pagination.prev_page}
            totalPages={pagination.total_pages}
          />
        </div>
      </div>
    </>
  )
}

export default Object.assign(FundsIndex, {
  layout: (page: ReactNode) => <DashboardLayout>{page}</DashboardLayout>,
})
